import { categories, type Category } from '@/mocks/categories';
import { services, type Service } from '@/mocks/services';

export type SortKey = 'latest' | 'hot' | 'recommended';

export interface FilterState {
  region: string;
  type: string;
  updated: string;
  sort: SortKey;
}

export const defaultFilters: FilterState = {
  region: '全部地区',
  type: '全部类型',
  updated: '全部时间',
  sort: 'latest',
};

export function getCategoryById(id: string | undefined): Category | undefined {
  if (!id) return undefined;
  return categories.find((item) => item.id === id);
}

export function getServiceById(id: string | undefined): Service | undefined {
  if (!id) return undefined;
  return services.find((item) => item.id === id);
}

export function getServicesByCategory(categoryId: string | undefined): Service[] {
  if (!categoryId) return [];
  return services.filter((item) => item.categoryId === categoryId);
}

export function getLatestServices(limit = 8): Service[] {
  return [...services]
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    .slice(0, limit);
}

export function getHotServices(limit = 8): Service[] {
  return [...services].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getFeaturedServices(): Service[] {
  return services.filter((item) => item.featured);
}

export function getRelatedServices(service: Service, limit = 4): Service[] {
  const sameCategory = services.filter(
    (item) => item.categoryId === service.categoryId && item.id !== service.id,
  );
  const sameTag = services.filter(
    (item) =>
      item.id !== service.id &&
      item.categoryId !== service.categoryId &&
      item.tags.some((tag) => service.tags.includes(tag)),
  );
  return [...sameCategory, ...sameTag].slice(0, limit);
}

export function searchServices(keyword: string): Service[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return services.filter((item) => {
    const haystack = [
      item.title,
      item.summary,
      item.subCategory,
      item.provider,
      item.type,
      item.region,
      ...item.tags,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(key);
  });
}

function isWithinUpdated(dateStr: string, updated: string): boolean {
  if (updated === '全部时间') return true;
  const days = updated === '近一周' ? 7 : updated === '近一月' ? 31 : 93;
  const diff = Date.now() - new Date(dateStr).getTime();
  return diff <= days * 24 * 60 * 60 * 1000;
}

export function applyFilters(list: Service[], filters: FilterState): Service[] {
  const filtered = list.filter((item) => {
    if (filters.region !== '全部地区' && item.region !== filters.region) return false;
    if (filters.type !== '全部类型' && item.type !== filters.type) return false;
    if (!isWithinUpdated(item.publishedAt, filters.updated)) return false;
    return true;
  });

  const sorted = [...filtered];
  if (filters.sort === 'latest') {
    sorted.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  } else if (filters.sort === 'hot') {
    sorted.sort((a, b) => b.views - a.views);
  } else {
    sorted.sort((a, b) => b.rating - a.rating || b.views - a.views);
  }
  return sorted;
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
}

export function formatDate(dateStr: string): string {
  return dateStr.replace(/-/g, ' / ');
}